import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft } from "@/lib/public-icons";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { orpcPublic } from "@/lib/orpc";
import { JobDetailMeta } from "@/routes/-components/job-detail-meta";
import { JobProse } from "@/routes/-components/job-prose";
import { JobsNotFound } from "@/routes/jobs-preview/$domain/-components/jobs-not-found";
import { JobsShell } from "../-components/jobs-shell";

export const Route = createFileRoute("/jobs-preview/$domain/$boardSlug/$jobSlug")({
  component: JobDetail,
});

/** One position, in full. */
function JobDetail() {
  const { domain, boardSlug, jobSlug } = Route.useParams();
  const page = useQuery({
    ...orpcPublic.careers.jobsByDomainJob.queryOptions({ input: { domain, boardSlug, jobSlug } }),
    retry: false,
  });

  if (page.isError) {
    return (
      <JobsShell domain={domain}>
        <JobsNotFound />
      </JobsShell>
    );
  }

  const job = page.data?.job;

  return (
    <JobsShell domain={domain} facilityName={page.data?.facility.name}>
      <Link
        className="text-muted-foreground hover:text-foreground mb-6 flex items-center gap-1 text-sm"
        params={{ domain, boardSlug }}
        to="/jobs-preview/$domain/$boardSlug"
      >
        <ChevronLeft className="size-4" />
        {page.data?.board.name ?? "Back"}
      </Link>

      {page.isPending ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-80" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : (
        <article className="space-y-8">
          <header className="space-y-3">
            <h1 className="text-3xl font-semibold tracking-tight">{job?.title}</h1>
            <JobDetailMeta job={job} />
          </header>

          <JobProse body={job?.description} heading="About the role" />
          <JobProse body={job?.responsibilities} heading="What you would do" />
          <JobProse body={job?.requirements} heading="What we are looking for" />

          {/* Applying happens on the careers site, which owns the applicant's account and the
              screening questions. The jobs site is a shop window; it deliberately does not carry a
              second application form. */}
          {page.data?.facility.careersSlug ? (
            <Button asChild>
              <a href={`/careers/${page.data.facility.careersSlug}/jobs/${job?.id}`}>
                Apply for this position
              </a>
            </Button>
          ) : null}
        </article>
      )}
    </JobsShell>
  );
}
